import React from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Soft duplicate warning card on the post-ad form.
 * Spec: WEB_SPEC_PAY_AT_STORE_AND_UPDATE3 §2.1 — NEVER block posting.
 * `matches`: [{id,title,category_slug,category_name,price,created_at,similarity}]
 */
export default function DuplicateWarningCard({ matches }) {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  const first = matches[0];
  const others = matches.slice(1);
  return (
    <div
      data-testid="duplicate-warning-card"
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4 md:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-amber-900 text-sm">
            Looks like you already have a similar ad live
          </div>
          <div className="mt-1 text-sm text-amber-800">
            <span className="font-semibold">&ldquo;{first.title}&rdquo;</span>
            {first.category_name && (
              <span className="text-amber-700"> · {first.category_name}</span>
            )}
            <Link
              to={`/ad/${first.id}`}
              target="_blank"
              rel="noreferrer"
              data-testid="duplicate-warning-open-first"
              className="ml-2 inline-flex items-center gap-1 text-amber-900 font-semibold underline"
            >
              open <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          {others.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-amber-800 cursor-pointer hover:underline">
                {others.length} more similar ad{others.length === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1 pl-1">
                {others.map((m) => (
                  <li key={m.id} className="text-xs text-amber-800">
                    <Link
                      to={`/ad/${m.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-amber-900"
                      data-testid={`duplicate-warning-open-${m.id}`}
                    >
                      {m.title}
                    </Link>
                    {m.category_name && (
                      <span className="text-amber-700"> · {m.category_name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div className="mt-2 text-xs text-amber-700">
            You can still post this ad — we just wanted to give you a heads-up.
          </div>
        </div>
      </div>
    </div>
  );
}
